import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  FunctioneleValuta,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup} from "@angular/forms";
import {debounceTime, filter, Observable, shareReplay, tap} from "rxjs";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {CommonModule} from "@angular/common";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-functionele-valuta',
  templateUrl: './functionele-valuta.component.html',
  styleUrls: ['./functionele-valuta.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class FunctioneleValutaComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  public functioneleValutaFg = new FormGroup({
    valutaCode: new FormControl<string | null>(null),
    gemiddeldeKoersFunctioneleValuta: new FormControl<number | null>(null),
    valutaFactorFunctioneleValuta: new FormControl<number | null>(null),
  });

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (!data.aangifte) return;
        const fv = data.aangifte.belastingBedrag?.functioneleValuta;
        if (fv) {
          this.functioneleValutaFg.patchValue(fv, {emitEvent: false});
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public optionsValutaCode: SelectFormControlItem<string>[] = [
    {value: "AED", label: "UAE Dirham"},
    {value: "AFN", label: "Afghaaanse Afghani"},
    {value: "ALL", label: "Albaanse Lek"},
    {value: "AMD", label: "Armeense Dram"},
    {value: "AMD", label: "Armeense Dram"},
    {value: "ANG", label: "Nederlands Antilliaanse Gulden"},
    {value: "AOA", label: "Angolses Kwanza"},
    {value: "ARS", label: "Argentijnse Peso"},
    {value: "AUD", label: "Australische Dollar"},
    {value: "AWG", label: "Arubanse Gulden"},
    {value: "AZN", label: "Azerbeidjaanse Manat"},
    {value: "BAM", label: "Convertible Mark"},
    {value: "BBD", label: "Barbados Dollar"},
    {value: "BDT", label: "Bangladeshi Taka"},
    {value: "BGN", label: "Bulgaarse Lev"},
    {value: "BHD", label: "Bahreinse Dinar"},
    {value: "BIF", label: "Burundi Franc"},
    {value: "BMD", label: "Bermudiaanse Dollar"},
    {value: "BND", label: "Brunei Dollar"},
    {value: "BRL", label: "Braziliaanse Real"},
    {value: "BSD", label: "Bahamiaanse Dollar"},
    {value: "BWP", label: "Botswana Pula"},
    {value: "BZD", label: "Belize Dollar"},
    {value: "CAD", label: "Canadese Dollar"},
    {value: "CDF", label: "Congolese Franc"},
    {value: "CHF", label: "Zwitsere Franc"},
    {value: "CLF", label: "Unidad de Fomento"},
    {value: "CLP", label: "Chileense Peso"},
    {value: "CNY", label: "Yuan Renminbi"},
    {value: "COP", label: "Colombische Peso"},
    {value: "COU", label: "Unidad de Valor Real"},
    {value: "CRC", label: "Costa Ricaanse Colon"},
    {value: "CUC", label: "Cubaanse Peso Convertible"},
    {value: "CVE", label: "Kaapverdiaanse Escudo"},
    {value: "CZK", label: "Tjechische Kroon"},
    {value: "DJF", label: "Djibouti Franc"},
    {value: "DKK", label: "Deense Kroon"},
    {value: "DOP", label: "Dominicaanse Peso"},
    {value: "DZD", label: "Algerijnse Dinar"},
    {value: "EGP", label: "Egyptische Pond"},
    {value: "ETB", label: "Ethiopische Birr"},
    {value: "FJD", label: "Fiji Dollar"},
    {value: "FKP", label: "Falkland Eilanden Pond"},
    {value: "GBP", label: "Pound Sterling"},
    {value: "GEL", label: "Lari"},
    {value: "GHS", label: "Ghana Cedi"},
    {value: "GIP", label: "Gibraltar Pond"},
    {value: "GMD", label: "Dalasi"},
    {value: "GNF", label: "Guinee Franc"},
    {value: "GTQ", label: "Quetzal"},
    {value: "GYD", label: "Guyana Dollar"},
    {value: "HKD", label: "Hong Kong Dollar"},
    {value: "HNL", label: "Lempira"},
    {value: "HRK", label: "Kuna"},
    {value: "HTG", label: "Gourde"},
    {value: "HUF", label: "Forint"},
    {value: "IDR", label: "Roepia"},
    {value: "ILS", label: "Nieuwe Israelische shekel"},
    {value: "INR", label: "Indiasche Roepie"},
    {value: "IQD", label: "Iraakse Dinar"},
    {value: "IRR", label: "Iraanse Rial"},
    {value: "ISK", label: "IJslandse Kroon"},
    {value: "JMD", label: "Jamaicaanse Dollar"},
    {value: "JOD", label: "Jordaanse Dinar"},
    {value: "JPY", label: "Yen"},
    {value: "KES", label: "Keniaanse Shilling"},
    {value: "KGS", label: "Som"},
    {value: "KHR", label: "Cambodja Riel"},
    {value: "KMF", label: "Comoro Franc"},
    {value: "KPW", label: "Koreaanse Won"},
    {value: "KWD", label: "Kuweiti Dinar"},
    {value: "KYD", label: "Kaaiman Eilanden Dollar"},
    {value: "KZT", label: "Tenge"},
    {value: "LAK", label: "Kip"},
    {value: "LBP", label: "Libanese Pond"},
    {value: "LKR", label: "Sri Lankese Roepie"},
    {value: "LRD", label: "Liberiaanse Dollar"},
    {value: "LSL", label: "Loti"},
    {value: "LYD", label: "Libische Dinar"},
    {value: "MAD", label: "Marokkaanse Dirham"},
    {value: "MDL", label: "Moldovaarse Leu"},
    {value: "MGA", label: "Malagasy Ariary"},
    {value: "MKD", label: "Denar"},
    {value: "MMK", label: "Kyat"},
    {value: "MNT", label: "Tugrik"},
    {value: "MOP", label: "Pataca"},
    {value: "MRO", label: "Ouguiya"},
    {value: "MUR", label: "Mauritius Roepie"},
    {value: "MVR", label: "Rufiyaa"},
    {value: "MWK", label: "Kwacha"},
    {value: "MXN", label: "Mexicaanse Peso"},
    {value: "MYR", label: "Maleisische Ringgit"},
    {value: "MZN", label: "Mozambique Metical"},
    {value: "NAD", label: "Namibie Dollar"},
    {value: "NGN", label: "Naira"},
    {value: "NIO", label: "Cordoba Oro"},
    {value: "NOK", label: "Noorse Kroon"},
    {value: "NPR", label: "Nepalese Roepie"},
    {value: "NZD", label: "Nieuw Zeelandse Dollar"},
    {value: "OMR", label: "Rial Omani"},
    {value: "PAB", label: "Balboa"},
    {value: "PEN", label: "Nuevo Sol"},
    {value: "PGK", label: "Kina"},
    {value: "PHP", label: "Filippijnse Peso"},
    {value: "PKR", label: "Pakistan Roepie"},
    {value: "PLN", label: "Zloty"},
    {value: "PYG", label: "Guarani"},
    {value: "QAR", label: "Qatari Rial"},
    {value: "RON", label: "Roemeense Leu"},
    {value: "RSD", label: "Servische Dinar"},
    {value: "RUB", label: "Russische Roebel"},
    {value: "RWF", label: "Rwandese Franc"},
    {value: "SAR", label: "Saudi Riyal"},
    {value: "SBD", label: "Solomon Eilanden Dollar"},
    {value: "SCR", label: "Seychellen Roepie"},
    {value: "SDG", label: "Soedanese Pond"},
    {value: "SEK", label: "Zweedse Kroon"},
    {value: "SGD", label: "Singapore Dollar"},
    {value: "SHP", label: "Saint Helena Pond"},
    {value: "SLL", label: "Leone"},
    {value: "SOS", label: "Somalische Shilling"},
    {value: "SRD", label: "Surinaamse Dollar"},
    {value: "SSP", label: "Zuid Soedanese Pond"},
    {value: "STD", label: "Dobra"},
    {value: "SVC", label: "El Salvador Colon"},
    {value: "SYP", label: "Syrische Pond"},
    {value: "SZL", label: "Lilangeni"},
    {value: "THB", label: "Baht"},
    {value: "TJS", label: "Somoni"},
    {value: "TMT", label: "Turkmeense Nieuwe Manat"},
    {value: "TND", label: "Tunisische Dinar"},
    {value: "TOP", label: "Pa’anga"},
    {value: "TRY", label: "Turkse Lira"},
    {value: "TTD", label: "Trinidad en Tobago Dollar"},
    {value: "TWD", label: "Nieuw Taiwanese Dollar"},
    {value: "TZS", label: "Tanzaniaanse Shilling"},
    {value: "UAH", label: "Hryvnia"},
    {value: "UGX", label: "Oegandese Shilling"},
    {value: "USD", label: "Amerikaanse Dollar"},
    {value: "UYI", label: "Uruguay Peso en Unidades Indexadas (URUIURUI)"},
    {value: "UYU", label: "Peso Uruguayo"},
    {value: "UZS", label: "Oezbekistan Sum"},
    {value: "VEF", label: "Bolivar"},
    {value: "VND", label: "Dong"},
    {value: "VUV", label: "Vatu"},
    {value: "WST", label: "Tala"},
    {value: "XAF", label: "CFA Franc BEAC"},
    {value: "XCD", label: "East Caribbean Dollar"},
    {value: "XOF", label: "CFA Franc BCEAO"},
    {value: "XPF", label: "CFP Franc"},
    {value: "YER", label: "Yemeni Rial"},
    {value: "ZAR", label: "Rand"},
    {value: "ZMW", label: "Zambiase Kwacha"},
    {value: "ZWL", label: "Zimbabwese Dollar"}
  ]

  ngOnInit(): void {
    this.functioneleValutaFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.functioneleValutaFg.updateValueAndValidity({emitEvent: false});
    const raw = this.functioneleValutaFg.getRawValue();
    const functioneleValuta = {
      valutaCode: raw.valutaCode ?? undefined,
      gemiddeldeKoersFunctioneleValuta: raw.gemiddeldeKoersFunctioneleValuta ?? undefined,
      valutaFactorFunctioneleValuta: raw.valutaFactorFunctioneleValuta ?? undefined,
    } as FunctioneleValuta;

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) return currentEntity;
      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          belastingBedrag: {
            ...currentEntity.aangifte.belastingBedrag,
            functioneleValuta,
          },
        } as VpbAangifte,
      };
    });
  }
}
